import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import SizePills from "./size-pills/index";
import {
  fetchAvailability as fetchReserveAvailability,
  fetchPricing as fetchReservePricing,
} from "slices/pdp-reserve-slice";
import AddToBagError from "./add-to-bag-error";
import { isActiveMembership } from "helpers/membership-helpers";
import FitRecommendation from "components/source/membership/fit/fit-recommendation";
import FitGuarantee from "../shared/fit-guarantee/fit-guarantee";
import { membershipLens } from "rtr-constants";
import { isOneSize } from "./helpers/disco-product-helper";
import AddressZipUpdater from "./address-zip-updater";
import { setSelectedSizeSku, setSecondSelectedSizeSku } from "slices/pdp-slice";

const ReserveBuyBox = () => {
  const [selectedSize, setSelectedSize] = useState(null);
  const [secondSelectedSize, setSecondSelectedSize] = useState(null);
  const staticProduct = useSelector(state => state.staticProduct);
  const membershipState = useSelector(state => state.membershipState);
  const workingFilters = useSelector(state => state.workingFilters) || {};

  const membershipStateLoading = useSelector(state => state.membershipStateLoading);

  const reserveAvailability = useSelector(state => ({
    availability: state.pdp?.reserve?.availability?.items,
    availabilityLoaded: state.pdp?.reserve?.availabilityLoaded,
    availabilityError: state.pdp?.reserve?.availabilityError,
  }));

  const dispatch = useDispatch();

  useEffect(() => {
    if (!staticProduct?.productId || !workingFilters.zip_code || !workingFilters.date || !workingFilters.duration)
      return;

    dispatch(
      fetchReserveAvailability({
        productId: staticProduct.productId,
        zipCode: workingFilters.zip_code,
        rentBeginDate: workingFilters.date,
        rentalDuration: workingFilters.duration,
      })
    );
  }, [
    workingFilters.zip_code,
    workingFilters.date,
    workingFilters.duration,
    workingFilters.canonicalSizes,
    staticProduct.productId,
  ]);

  useEffect(() => {
    if (membershipStateLoading === false) {
      dispatch(
        fetchReservePricing({
          productId: staticProduct.productId,
          rentalDuration: 4,
          activeMember: isActiveMembership(membershipState),
        })
      );
    }
  }, [membershipStateLoading, membershipState]);

  const handleSizeSelect = size => {
    setSelectedSize(size);
    if (size?.sku) {
      dispatch(setSelectedSizeSku(size.sku));
    }
  };

  const handleSecondSizeSelect = size => {
    setSecondSelectedSize(size);
    if (size?.sku) {
      dispatch(setSecondSelectedSizeSku(size.sku));
    }
  };

  const oneSizeProduct = isOneSize(staticProduct);
  const isDuplicateSizeSelection = secondSelectedSize && selectedSize === secondSelectedSize;

  return (
    <section className="mt-6" data-test-id="reserve-buy-box" aria-labelledby="reserve-add-to-bag">
      <SizePills
        skus={staticProduct.skus}
        availability={reserveAvailability.availability}
        availabilityLoaded={reserveAvailability.availabilityLoaded}
        availabilityError={reserveAvailability.availabilityError}
        onSelect={handleSizeSelect}
        hideOneSize={oneSizeProduct}
        selectUnavailableSize={false}
        dataTestId="primary-size-pills"
      />

      {!oneSizeProduct && (
        <>
          <SizePills
            skus={staticProduct.skus}
            availability={reserveAvailability.availability}
            availabilityLoaded={reserveAvailability.availabilityLoaded}
            availabilityError={
              isDuplicateSizeSelection ? "Select a different size" : reserveAvailability.availabilityError
            }
            noPreselect={true}
            onSelect={handleSecondSizeSelect}
            selectUnavailableSize={false}
            label="Free Backup Size"
            errorSku={isDuplicateSizeSelection ? secondSelectedSize?.sku : null}
            dataTestId="backup-size-pills"
          />
        </>
      )}

      <FitRecommendation product={staticProduct} />
      <AddToBagError />
      <FitGuarantee lens={membershipLens.unlimited} placement="pdp" />
      <AddressZipUpdater isGridProductDrawer={false} />
    </section>
  );
};

export default ReserveBuyBox;
